/**
 * Location Helper Utility
 * Provides functions to fetch countries, states, and cities.
 * Designed for CRM Genix to use in dropdowns and forms.
 */

export interface Country {
  name: string;
  iso2?: string;
  iso3?: string;
}

export interface State {
  name: string;
  state_code?: string;
}

export interface City {
  name: string;
}

/**
 * 🌍 Fetch all countries
 */
export const getCountries = async (): Promise<Country[]> => {
  try {
    const res = await fetch("https://countriesnow.space/api/v0.1/countries/positions");
    const data = await res.json();

    if (!data || !data.data) throw new Error("Invalid country response");

    return data.data.map((item: any) => ({
      name: item.name,
      iso2: item.iso2,
      iso3: item.iso3,
    }));
  } catch (err) {
    console.error("Error fetching countries:", err);
    return [];
  }
};

/**
 * 🏙️ Fetch states by country name
 */
export const getStatesByCountry = async (country: string): Promise<State[]> => {
  try {
    const res = await fetch("https://countriesnow.space/api/v0.1/countries/states", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ country }),
    });
    const data = await res.json();

    if (!data?.data?.states) throw new Error("Invalid state response");

    return data.data.states.map((state: any) => ({
      name: state.name,
      state_code: state.state_code,
    }));
  } catch (err) {
    console.error(`Error fetching states for ${country}:`, err);
    return [];
  }
};

/**
 * 🌆 Fetch cities by country and state
 */
export const getCitiesByState = async (
  country: string,
  state: string
): Promise<City[]> => {
  try {
    const res = await fetch("https://countriesnow.space/api/v0.1/countries/state/cities", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ country, state }),
    });
    const data = await res.json();

    if (!data?.data) throw new Error("Invalid city response");

    return data.data.map((city: string) => ({
      name: city,
    }));
  } catch (err) {
    console.error(`Error fetching cities for ${state}, ${country}:`, err);
    return [];
  }
};
